import React, { useRef } from "react";
import { Box, Text, Textarea, Wrap, WrapItem, Tag, Divider } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { renderTemplatePreview, splitBoldSegments } from "utils/templatePreview";

// Matches Bot/templateEngine.js's KEYS: the original single-locale set
// (resolved to whichever locale formatMessage.js prefers — kept for
// backward compatibility with existing templates) plus explicit _ar/_en
// variants so one template can show both languages in the same message.
const PLACEHOLDER_GROUPS = [
  { label: "Any locale", keys: ["title", "header", "text_preview", "link", "category", "date"] },
  { label: "Arabic", keys: ["title_ar", "header_ar", "text_preview_ar", "category_ar", "link_ar"] },
  { label: "English", keys: ["title_en", "header_en", "text_preview_en", "category_en", "link_en"] },
];

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

// Shared by CreateBotTemplate and the edit modal in ListBotTemplate so the
// placeholder legend + live preview behave identically in both places.
function TemplateContentEditor({ value, onChange, isInvalid, errorMessage }) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    const token = `{${placeholder}}`;
    if (!textarea) {
      onChange(`${value || ""}${token}`);
      return;
    }
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    onChange(next);
    // Restore focus + cursor position after the inserted token on the next tick.
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const previewText = renderTemplatePreview(value);

  return (
    <Box mt="16px">
      <Text fontSize="sm" fontWeight="500" color={GOLD_TEXT} mb="2">
        {t("Content")}
      </Text>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        fontFamily="mono"
        isInvalid={isInvalid}
        {...fieldProps}
      />
      {errorMessage && (
        <Text color="red.500" fontSize="xs" mt="4px">
          {errorMessage}
        </Text>
      )}

      {PLACEHOLDER_GROUPS.map((group) => (
        <Box key={group.label} mt="8px">
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb="4px">
            {t(group.label)}
          </Text>
          <Wrap spacing="6px">
            {group.keys.map((placeholder) => (
              <WrapItem key={placeholder}>
                <Tag
                  size="sm"
                  cursor="pointer"
                  bg={GOLD_LIGHT}
                  color={GOLD_TEXT}
                  fontFamily="mono"
                  _hover={{ bg: GOLD_BORDER, color: "#1A1A1A" }}
                  onClick={() => insertPlaceholder(placeholder)}
                >
                  {`{${placeholder}}`}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      ))}
      <Text fontSize="xs" color="gray.500" mt="8px">
        {t("Click a placeholder to insert it. Missing values render empty and extra blank lines collapse automatically. \"Any locale\" resolves to whichever language is preferred; the Arabic/English ones let one message show both.")}
      </Text>
      <Text fontSize="xs" color="gray.500" mt="4px">
        {t("Wrap text in **double asterisks** to make it bold — works on both Telegram and WhatsApp.")}
      </Text>

      <Divider my="14px" />

      <Text fontSize="sm" fontWeight="500" color={GOLD_TEXT} mb="2">
        {t("Live Preview")}
      </Text>
      <Box
        bg={GOLD_LIGHT}
        border="1px solid"
        borderColor={GOLD_BORDER}
        borderRadius="md"
        p="12px"
        whiteSpace="pre-wrap"
        fontSize="sm"
        minH="60px"
      >
        {previewText
          ? splitBoldSegments(previewText).map((segment, index) =>
              segment.bold ? (
                <Text as="b" key={index}>{segment.text}</Text>
              ) : (
                <React.Fragment key={index}>{segment.text}</React.Fragment>
              )
            )
          : t("(empty)")}
      </Box>
    </Box>
  );
}

export default TemplateContentEditor;
